import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Store, Bookmark, Star, Search, ChevronRight,
  ShoppingBag, MapPin,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

/**
 * EShopSection — Horizontal carousel of shops, displayed in the Studio home tab
 * just below the Podcasts section.
 *
 * Features:
 *  - Country-prioritized + randomized feed (algorithm in backend)
 *  - Category filter ("Tout" + dynamic categories)
 *  - Search bar (shop name / description)
 *  - Like / Save buttons on each shop card
 *  - Star rating display
 *  - Tap → go to /shop/:id
 */
const CAT_LABEL_KEY = {
  all: 'eshop.cat_all',
  fashion: 'eshop.cat_fashion',
  beauty: 'eshop.cat_beauty',
  food: 'eshop.cat_food',
  myfood: 'eshop.cat_myfood',
  art: 'eshop.cat_art',
  music: 'eshop.cat_music',
  books: 'eshop.cat_books',
  electronics: 'eshop.cat_electronics',
  home: 'eshop.cat_home',
  handcraft: 'eshop.cat_handcraft',
  wellness: 'eshop.cat_wellness',
  services: 'eshop.cat_services',
  education: 'eshop.cat_education',
  kids: 'eshop.cat_kids',
  sport: 'eshop.cat_sport',
  jewelry: 'eshop.cat_jewelry',
  other: 'eshop.cat_other',
};

const EShopSection = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const queryTimer = useRef(null);

  useEffect(() => {
    loadShops();
  }, [language, category]); // eslint-disable-line

  const loadShops = useCallback(async (qOverride) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ language, per_page: '20' });
      if (category && category !== 'all') params.set('category', category);
      const q = qOverride !== undefined ? qOverride : query;
      if (q.trim()) params.set('q', q.trim());
      const res = await axios.get(`/api/shop/feed?${params.toString()}`);
      setShops(res.data?.shops || []);
    } catch {
      setShops([]);
    } finally {
      setLoading(false);
    }
  }, [language, category, query]);

  const handleSearchChange = (val) => {
    setQuery(val);
    if (queryTimer.current) clearTimeout(queryTimer.current);
    queryTimer.current = setTimeout(() => loadShops(val), 350);
  };

  const handleSave = async (shopId, e) => {
    e.stopPropagation();
    try {
      const r = await axios.post(`/api/shop/shops/${shopId}/save`);
      setShops(prev => prev.map(s => s.id === shopId
        ? { ...s, viewer_saved: r.data.saved, saves_count: s.saves_count + (r.data.saved ? 1 : -1) }
        : s));
    } catch {}
  };

  const cats = ['all', 'fashion', 'beauty', 'myfood', 'art', 'music', 'handcraft',
                'electronics', 'home', 'wellness', 'services', 'education',
                'kids', 'sport', 'jewelry', 'books', 'other'];

  return (
    <div data-testid="eshop-section">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Store className="w-5 h-5 text-fuchsia-400" />
          {t('eshop.title') || 'MyShop'}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSearch(s => !s)}
            className={`p-2 rounded-full ${showSearch ? 'bg-fuchsia-600' : 'bg-white/10 hover:bg-white/20'}`}
            data-testid="eshop-toggle-search-btn"
            aria-label="Rechercher"
          >
            <Search className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/shop')}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs font-semibold"
            data-testid="eshop-see-all-btn"
          >
            {t('eshop.see_all') || 'Tout voir'} <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Search bar */}
      {showSearch && (
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={query}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder={t('eshop.search_placeholder') || 'Rechercher une boutique ou un produit…'}
              className="w-full bg-white/5 border border-white/10 rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-fuchsia-500"
              data-testid="eshop-search-input"
            />
          </div>
        </div>
      )}

      {/* Categories chip row */}
      <div className="flex overflow-x-auto gap-1.5 mb-3 -mx-4 px-4 scrollbar-hide" data-testid="eshop-categories">
        {cats.map(c => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              category === c
                ? 'bg-fuchsia-600 text-white'
                : 'bg-white/5 text-gray-300 hover:bg-white/10'
            }`}
            data-testid={`eshop-cat-${c}`}
          >
            {t(CAT_LABEL_KEY[c] || `eshop.cat_${c}`) || c}
          </button>
        ))}
      </div>

      {/* Horizontal carousel */}
      {loading ? (
        <div className="flex gap-3 overflow-hidden -mx-4 px-4">
          {[1,2,3].map(i => (
            <div key={i} className="shrink-0 w-[240px] h-[280px] rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : shops.length === 0 ? (
        <div className="text-center py-8 rounded-2xl bg-white/5 border border-white/10" data-testid="eshop-empty">
          <ShoppingBag className="w-10 h-10 mx-auto mb-2 text-gray-500" />
          <p className="text-sm text-gray-400">{t('eshop.empty') || 'Aucune boutique pour le moment'}</p>
        </div>
      ) : (
        <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 -mx-4 px-4 pb-1 scrollbar-hide" data-testid="eshop-carousel">
          {shops.map(shop => (
            <button
              key={shop.id}
              onClick={() => navigate(`/shop/${shop.id}`)}
              className="shrink-0 w-[240px] snap-center bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-2xl overflow-hidden text-left hover:border-fuchsia-500/40 transition-colors"
              data-testid={`eshop-card-${shop.id}`}
            >
              {/* Cover */}
              <div className="relative h-[140px] bg-gradient-to-br from-fuchsia-700/30 to-violet-800/30">
                {shop.cover_url ? (
                  <img
                    src={getMediaUrl(shop.cover_url)}
                    alt={shop.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Store className="w-10 h-10 text-white/30" />
                  </div>
                )}
                {/* Country flag pill */}
                {shop.country && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur text-[10px] font-semibold text-white">
                    {shop.country}
                  </span>
                )}
                {/* Save floating */}
                <div className="absolute top-2 right-2 flex gap-1.5">
                  <button
                    onClick={(e) => handleSave(shop.id, e)}
                    className="p-1.5 rounded-full bg-black/50 backdrop-blur hover:bg-black/70"
                    data-testid={`eshop-save-${shop.id}`}
                    aria-label="Enregistrer"
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${shop.viewer_saved ? 'fill-yellow-400 text-yellow-400' : 'text-white'}`} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-sm leading-tight truncate flex-1">{shop.name}</h4>
                </div>
                <p className="text-[11px] text-gray-400 line-clamp-2 leading-snug">
                  {shop.description || (t('eshop.no_description') || 'Pas de description')}
                </p>
                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                  {shop.rating_count > 0 ? (
                    <span className="flex items-center gap-0.5">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      <span className="text-white font-semibold">{shop.rating_avg?.toFixed?.(1) || shop.rating_avg}</span>
                      <span>· {shop.rating_count} {shop.rating_count > 1 ? (t('eshop.reviews_count') || 'avis') : (t('eshop.review_one') || 'avis')}</span>
                    </span>
                  ) : (
                    <span className="text-gray-500">{t('eshop.no_reviews_short') || 'Pas d\'avis'}</span>
                  )}
                  {shop.address && (
                    <>
                      <span className="opacity-50">·</span>
                      <span className="flex items-center gap-0.5 truncate">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{shop.address.slice(0, 14)}</span>
                      </span>
                    </>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EShopSection;
